import type { PropertyDef } from "@/hooks/useProperties";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { useState } from "react";

// SIM-1443: shared dashboard-edit form body used by the index-card modal, the Settings → Dashboards
// row and the Add Dashboard page; each parent owns its chrome, and the only internal state is the
// "+ Add option…" dialog. SIM-1464: year and category read their options from PropertyDef like
// every other typed property. The sentinel marking the "+ Add" row in the Radix Select is a
// non-printable token, chosen so it can never collide with a real option value.
const ADD_OPTION_SENTINEL = "__add-new-option__";

export type DashboardType = "looker" | "sheets" | "spark";

export type TagMap = Record<string, string[]>;

export function tagsToMap(tags: Array<{ propertyKey: string; value: string }>): TagMap {
  const map: TagMap = {};
  for (const t of tags) {
    const list = map[t.propertyKey] ?? [];
    list.push(t.value);
    map[t.propertyKey] = list;
  }
  return map;
}

export function mapToTags(map: TagMap): Array<{ propertyKey: string; value: string }> {
  const out: Array<{ propertyKey: string; value: string }> = [];
  for (const [propertyKey, values] of Object.entries(map)) {
    for (const value of values) out.push({ propertyKey, value });
  }
  return out;
}

export function tagMapsEqual(a: TagMap, b: TagMap): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    const av = (a[k] ?? []).slice().sort();
    const bv = (b[k] ?? []).slice().sort();
    if (av.length !== bv.length) return false;
    for (let i = 0; i < av.length; i++) {
      if (av[i] !== bv[i]) return false;
    }
  }
  return true;
}

// SIM-1464 — year/category option lists come from the PropertyDef rows
// auto-seeded for the `school_year` and `category` keys. Returns the empty
// array when the matching PropertyDef doesn't exist (e.g. in tests that
// pass `properties=[]`) so callers don't have to special-case.
export function findPropertyOptions(properties: PropertyDef[], key: string): string[] {
  return properties.find((p) => p.key === key)?.options ?? [];
}

// SIM-1443 — URL is not user-meaningful for SimplySpark dashboards (their
// content is rendered internally via `sparkDashboardId`), so we don't gate
// save on a non-empty URL when `type === "spark"`. The URL Input is also
// hidden in that case (see <DashboardEditFields>).
export function canSaveDashboardEdit({
  title,
  url,
  type,
}: {
  title: string;
  url: string;
  type: DashboardType;
}): boolean {
  return title.trim().length > 0 && (type === "spark" || url.trim().length > 0);
}

function PropertyValueInput({
  property,
  selectedValues,
  onChange,
  dashboardId,
  onRequestAddOption,
}: {
  property: PropertyDef;
  selectedValues: string[];
  onChange: (next: string[]) => void;
  dashboardId: string;
  onRequestAddOption?: () => void;
}) {
  if (property.type === "single-select") {
    const value = selectedValues[0] ?? "";
    return (
      <Select
        value={value}
        onValueChange={(v) => {
          if (v === ADD_OPTION_SENTINEL) {
            onRequestAddOption?.();
            return;
          }
          onChange(v ? [v] : []);
        }}
      >
        <SelectTrigger id={`tag-${property.key}-${dashboardId}`} size="sm">
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {property.options.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
          {value && !property.options.includes(value) && (
            <SelectItem value={value}>{value}</SelectItem>
          )}
          {onRequestAddOption && <SelectItem value={ADD_OPTION_SENTINEL}>+ Add option…</SelectItem>}
        </SelectContent>
      </Select>
    );
  }
  // SIM-1465 — multi-select branch now uses the shared <MultiSelect>
  // primitive. The popover handles its own search + checkbox list;
  // `onAddOption` opens the inherited <AddOptionDialog> (the inline
  // query value is intentionally discarded — the dialog re-prompts so
  // the year/category and per-property flows stay symmetrical).
  return (
    <MultiSelect
      id={`tag-${property.key}-${dashboardId}`}
      value={selectedValues}
      onChange={onChange}
      options={property.options}
      placeholder="—"
      onAddOption={onRequestAddOption ? () => onRequestAddOption() : undefined}
    />
  );
}

/**
 * SIM-1421 — modal dialog for entering the value of a new PropertyDef option. Open state lifted to
 * <DashboardEditFields> so the same dialog serves both the single-select dropdown sentinel and the
 * multi-select "+ Add option…" button — and (post SIM-1464) the year/category hardcoded dropdowns
 * above the Properties section.
 */
function AddOptionDialog({
  open,
  propertyLabel,
  value,
  onValueChange,
  onSubmit,
  onClose,
  submitting,
}: {
  open: boolean;
  propertyLabel: string;
  value: string;
  onValueChange: (v: string) => void;
  onSubmit: () => void;
  onClose: () => void;
  submitting: boolean;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Add option to {propertyLabel}</DialogTitle>
        </DialogHeader>
        <Input
          // eslint-disable-next-line jsx-a11y/no-autofocus -- inside <Dialog>; focus initial field on open (WCAG-acceptable modal pattern)
          autoFocus
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSubmit();
            }
          }}
          placeholder="New option"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={!value.trim() || submitting}>
            {submitting ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * SIM-1421 — `school_year` and `category` are still edited via the
 * dedicated <YearCategoryPair> at the top of the form (legacy hardcoded
 * UX). Filtering them out here avoids rendering them twice when the
 * tenant has the auto-seeded PropertyDef rows for those keys.
 */
function isLegacyMetadataProperty(key: string): boolean {
  return key === "school_year" || key === "category";
}

function PropertiesSubSection({
  dashboardId,
  properties,
  tagMap,
  setTagMap,
  variant,
  onRequestAddOption,
}: {
  dashboardId: string;
  properties: PropertyDef[];
  tagMap: TagMap;
  setTagMap: (m: TagMap) => void;
  variant: Variant;
  onRequestAddOption?: (propertyKey: string) => void;
}) {
  const propsToShow = properties.filter((p) => !isLegacyMetadataProperty(p.key));

  if (propsToShow.length === 0) return null;

  // SIM-1463 — no "Properties" heading + no border separator. Year/category
  // are also properties now (post-SIM-1421); rendering all of them as
  // equal-rank labeled fields avoids implying a hierarchy that no longer
  // exists in the data model.
  return (
    <div className={variant === "inline" ? "space-y-2" : "space-y-3"}>
      {propsToShow.map((p) => (
        <div key={p.key} className="space-y-1">
          <Label
            htmlFor={`tag-${p.key}-${dashboardId}`}
            className="text-xs font-medium text-foreground"
          >
            {p.label}
          </Label>
          <PropertyValueInput
            property={p}
            selectedValues={tagMap[p.key] ?? []}
            onChange={(next) => setTagMap({ ...tagMap, [p.key]: next })}
            dashboardId={dashboardId}
            onRequestAddOption={onRequestAddOption ? () => onRequestAddOption(p.key) : undefined}
          />
        </div>
      ))}
    </div>
  );
}

type Variant = "modal" | "inline";

function YearCategoryPair({
  dashboardId,
  variant,
  editSchoolYear,
  setEditSchoolYear,
  editCategory,
  setEditCategory,
  schoolYears,
  categories,
  onAddSchoolYear,
  onAddCategory,
}: {
  dashboardId: string;
  variant: Variant;
  editSchoolYear: string;
  setEditSchoolYear: (v: string) => void;
  editCategory: string;
  setEditCategory: (v: string) => void;
  schoolYears: string[];
  categories: string[];
  onAddSchoolYear?: () => void;
  onAddCategory?: () => void;
}) {
  if (variant === "inline") {
    return (
      <div className="flex gap-2">
        <SelectField
          value={editSchoolYear}
          onChange={setEditSchoolYear}
          options={schoolYears}
          onRequestAddOption={onAddSchoolYear}
        />
        <SelectField
          value={editCategory}
          onChange={setEditCategory}
          options={categories}
          onRequestAddOption={onAddCategory}
        />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3">
      <LabeledSelect
        id={`edit-year-${dashboardId}`}
        label="School Year"
        value={editSchoolYear}
        options={schoolYears}
        onChange={setEditSchoolYear}
        onRequestAddOption={onAddSchoolYear}
      />
      <LabeledSelect
        id={`edit-category-${dashboardId}`}
        label="Category"
        value={editCategory}
        options={categories}
        onChange={setEditCategory}
        onRequestAddOption={onAddCategory}
      />
    </div>
  );
}

function LabeledSelect({
  id,
  label,
  value,
  options,
  onChange,
  onRequestAddOption,
}: {
  id: string;
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  onRequestAddOption?: () => void;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs font-medium text-foreground">
        {label}
      </Label>
      <SelectField
        value={value}
        onChange={onChange}
        options={options}
        id={id}
        onRequestAddOption={onRequestAddOption}
      />
    </div>
  );
}

function SelectField({
  id,
  value,
  onChange,
  options,
  onRequestAddOption,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  onRequestAddOption?: () => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(v) => {
        if (v === ADD_OPTION_SENTINEL) {
          onRequestAddOption?.();
          return;
        }
        onChange(v);
      }}
    >
      <SelectTrigger id={id} size="sm" className="text-xs">
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {o}
          </SelectItem>
        ))}
        {value && !options.includes(value) && <SelectItem value={value}>{value}</SelectItem>}
        {onRequestAddOption && <SelectItem value={ADD_OPTION_SENTINEL}>+ Add option…</SelectItem>}
      </SelectContent>
    </Select>
  );
}

export interface DashboardEditFieldsProps {
  dashboardId: string;
  dashboardType: DashboardType;
  editTitle: string;
  setEditTitle: (v: string) => void;
  editUrl: string;
  setEditUrl: (v: string) => void;
  editSchoolYear: string;
  setEditSchoolYear: (v: string) => void;
  editCategory: string;
  setEditCategory: (v: string) => void;
  properties: PropertyDef[];
  tagMap: TagMap;
  setTagMap: (m: TagMap) => void;
  variant?: Variant;
  // Inline variant uses Enter to commit + Escape to cancel; modal handles
  // that via the dialog footer buttons. Optional in either case.
  onKeyDown?: (e: React.KeyboardEvent) => void;
  autoFocus?: boolean;
  // SIM-1421: when passed, every property's dropdown exposes a "+ Add option…" affordance. The
  // callback PATCHes `PropertyDef.options` with the appended value; the component then auto-selects
  // it — in the local tagMap for normal properties (multi-select appends, single-select replaces),
  // or in editSchoolYear / editCategory for year and category, which pass keys `school_year` and
  // `category` (SIM-1464).
  onAddPropertyOption?: (propertyKey: string, newValue: string) => Promise<void> | void;
}

function LabeledInput({
  id,
  label,
  showLabel,
  ...inputProps
}: {
  id: string;
  label: string;
  showLabel: boolean;
} & React.ComponentProps<typeof Input>) {
  if (!showLabel) return <Input id={id} {...inputProps} />;
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs font-medium text-foreground">
        {label}
      </Label>
      <Input id={id} {...inputProps} />
    </div>
  );
}

function useAddOptionDialog(opts: {
  properties: PropertyDef[];
  tagMap: TagMap;
  setTagMap: (m: TagMap) => void;
  setEditSchoolYear: (v: string) => void;
  setEditCategory: (v: string) => void;
  onAddPropertyOption?: (propertyKey: string, newValue: string) => Promise<void> | void;
}) {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const activeProperty = activeKey
    ? (opts.properties.find((p) => p.key === activeKey) ?? null)
    : null;

  const close = () => {
    setActiveKey(null);
    setValue("");
  };

  const submit = async () => {
    const trimmed = value.trim();
    if (!trimmed || !activeKey || !opts.onAddPropertyOption) return;
    setSubmitting(true);
    try {
      await opts.onAddPropertyOption(activeKey, trimmed);
      // Auto-select the new value in whichever surface owns this property.
      if (activeKey === "school_year") {
        opts.setEditSchoolYear(trimmed);
      } else if (activeKey === "category") {
        opts.setEditCategory(trimmed);
      } else if (activeProperty) {
        const existing = opts.tagMap[activeKey] ?? [];
        const next = activeProperty.type === "single-select" ? [trimmed] : [...existing, trimmed];
        opts.setTagMap({ ...opts.tagMap, [activeKey]: next });
      }
      close();
    } finally {
      setSubmitting(false);
    }
  };

  // Year/category PropertyDefs may have any label; fall back to the key if
  // the tenant hasn't been backfilled yet (defensive; the slice-3 migration
  // ensures the rows exist for every tenant in production).
  const propertyLabel = activeProperty?.label ?? activeKey ?? "";

  return {
    open: activeKey !== null,
    propertyLabel,
    value,
    setValue,
    submit,
    close,
    submitting,
    requestOpen: (key: string) => {
      setActiveKey(key);
      setValue("");
    },
  };
}

function TitleAndUrlFields({
  dashboardId,
  showUrl,
  showLabels,
  editTitle,
  setEditTitle,
  editUrl,
  setEditUrl,
  titleClass,
  urlClass,
  compact,
  onKeyDown,
  autoFocus,
}: {
  dashboardId: string;
  showUrl: boolean;
  showLabels: boolean;
  editTitle: string;
  setEditTitle: (v: string) => void;
  editUrl: string;
  setEditUrl: (v: string) => void;
  titleClass: string | undefined;
  urlClass: string | undefined;
  compact: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  autoFocus?: boolean;
}) {
  return (
    <>
      <LabeledInput
        id={`edit-title-${dashboardId}`}
        label="Name"
        showLabel={showLabels}
        value={editTitle}
        onChange={(e) => setEditTitle(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Dashboard name"
        className={titleClass}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- pass-through; the two callers (modal AddOptionDialog and inline edit-in-place) both have user-initiated focus contexts
        autoFocus={autoFocus}
      />
      {showUrl && (
        <LabeledInput
          id={`edit-url-${dashboardId}`}
          label="URL"
          showLabel={showLabels}
          value={editUrl}
          onChange={(e) => setEditUrl(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={compact ? "Dashboard URL" : "https://lookerstudio.google.com/..."}
          className={urlClass}
        />
      )}
    </>
  );
}

export function DashboardEditFields({
  dashboardId,
  dashboardType,
  editTitle,
  setEditTitle,
  editUrl,
  setEditUrl,
  editSchoolYear,
  setEditSchoolYear,
  editCategory,
  setEditCategory,
  properties,
  tagMap,
  setTagMap,
  variant = "modal",
  onKeyDown,
  autoFocus,
  onAddPropertyOption,
}: DashboardEditFieldsProps) {
  const compact = variant === "inline";
  const showLabels = !compact;
  const titleClass = compact ? "h-auto px-2 py-1 text-sm" : undefined;
  const urlClass = compact ? "h-auto px-2 py-1 text-xs text-muted-foreground" : undefined;
  const showUrl = dashboardType !== "spark";

  // SIM-1464 — year/category option lists are derived from the PropertyDef
  // rows for `school_year` and `category` (auto-seeded per tenant). The
  // legacy PortalSetting reads were removed in this slice.
  const schoolYears = findPropertyOptions(properties, "school_year");
  const categories = findPropertyOptions(properties, "category");

  const addDialog = useAddOptionDialog({
    properties,
    tagMap,
    setTagMap,
    setEditSchoolYear,
    setEditCategory,
    onAddPropertyOption,
  });

  const onAddSchoolYear = onAddPropertyOption
    ? () => addDialog.requestOpen("school_year")
    : undefined;
  const onAddCategory = onAddPropertyOption ? () => addDialog.requestOpen("category") : undefined;
  const onRequestAddOption = onAddPropertyOption ? addDialog.requestOpen : undefined;

  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      <TitleAndUrlFields
        dashboardId={dashboardId}
        showUrl={showUrl}
        showLabels={showLabels}
        editTitle={editTitle}
        setEditTitle={setEditTitle}
        editUrl={editUrl}
        setEditUrl={setEditUrl}
        titleClass={titleClass}
        urlClass={urlClass}
        compact={compact}
        onKeyDown={onKeyDown}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- pass-through; the two callers (modal AddOptionDialog and inline edit-in-place) both have user-initiated focus contexts
        autoFocus={autoFocus}
      />
      <YearCategoryPair
        dashboardId={dashboardId}
        variant={variant}
        editSchoolYear={editSchoolYear}
        setEditSchoolYear={setEditSchoolYear}
        editCategory={editCategory}
        setEditCategory={setEditCategory}
        schoolYears={schoolYears}
        categories={categories}
        onAddSchoolYear={onAddSchoolYear}
        onAddCategory={onAddCategory}
      />
      <PropertiesSubSection
        dashboardId={dashboardId}
        properties={properties}
        tagMap={tagMap}
        setTagMap={setTagMap}
        variant={variant}
        onRequestAddOption={onRequestAddOption}
      />
      <AddOptionDialog
        open={addDialog.open}
        propertyLabel={addDialog.propertyLabel}
        value={addDialog.value}
        onValueChange={addDialog.setValue}
        onSubmit={() => void addDialog.submit()}
        onClose={addDialog.close}
        submitting={addDialog.submitting}
      />
    </div>
  );
}
